import React, { useRef, useState, useLayoutEffect } from "react";
import {
  Treemap,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Component, ChartConfig } from "@/types";
import { getChartSizeConfig, getChartResponsiveConfig } from "@/utils/chart-responsive-utils";
import { useResponsiveViewport } from "@/hooks/useResponsiveViewport";
import { useQueryTemplateData, isQueryTemplateDataSource, getXAxisKeyFromQuery, getLineKeysFromQuery } from "@/hooks/useQueryTemplateData";
import { ChartDataSource } from "@/types/chartDataSource";
import { resolveColorArray } from "@/utils/cssVariableResolver";
import { AlertCircle, RefreshCw } from "lucide-react";
import { useChartNavigation } from "@/hooks/useChartNavigation";
import { useComponentInteraction } from "@/components/shared/rendering/InteractionContextRuntime";
import { NavigationIndicator } from "./NavigationIndicator";
import { ChartLoadingSkeleton } from "@/components/common/DataLoadingSkeletons";

interface TreemapChartProps {
  component: Component;
  isPreview?: boolean;
}

type TreemapDatum = Record<string, unknown> & {
  name?: string;
  size?: number;
  value?: number;
  children?: TreemapDatum[];
};

type TreemapCellPayload = TreemapDatum & {
  depth?: number;
  index?: number;
};

interface CustomizedContentProps extends TreemapDatum {
  depth?: number;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  index?: number;
  colors?: string[];
  valueKey?: string;
  onCellClick?: (payload: TreemapCellPayload) => void;
  isNavigable?: boolean;
  getNavigationForElement?: (seriesKey?: string, dataPointValue?: string) => unknown;
}

const toNumericDimension = (value: unknown): number | undefined =>
  typeof value === "number" ? value : undefined;

const defaultData = [
  {
    name: 'Category A',
    size: 3000,
    children: [
      { name: 'A1', size: 1500 },
      { name: 'A2', size: 1000 },
      { name: 'A3', size: 500 },
    ],
  },
  {
    name: 'Category B',
    size: 2500,
    children: [
      { name: 'B1', size: 1200 },
      { name: 'B2', size: 800 },
      { name: 'B3', size: 500 },
    ],
  },
  {
    name: 'Category C',
    size: 2000,
    children: [
      { name: 'C1', size: 1000 },
      { name: 'C2', size: 600 },
      { name: 'C3', size: 400 },
    ],
  },
  {
    name: 'Category D',
    size: 1500,
    children: [
      { name: 'D1', size: 800 },
      { name: 'D2', size: 700 },
    ],
  },
];

const defaultColors = [
  "#8884d8",
  "#82ca9d",
  "#ffc658",
  "#ff7300",
  "#8dd1e1",
  "#d084d0",
];

// Custom content renderer for treemap cells
const CustomizedContent: React.FC<CustomizedContentProps> = (props) => {
  const {
    depth,
    x,
    y,
    width,
    height,
    index = 0,
    colors = defaultColors,
    name,
    valueKey = 'size',
    // Navigation props
    onCellClick,
    isNavigable,
    getNavigationForElement,
  } = props;

  // Get the value using the dynamic valueKey
  const value = props[valueKey] ?? props.size ?? props.value;
  const cellX = x ?? 0;
  const cellY = y ?? 0;
  const cellWidth = width ?? 0;
  const cellHeight = height ?? 0;
  const cellDepth = depth ?? 0;

  // Dynamic font sizing based on cell dimensions
  const fontSize = cellWidth > 120 ? 14 : cellWidth > 80 ? 12 : cellWidth > 50 ? 10 : 0;
  const showValue = cellHeight > 40 && fontSize > 0;

  // Truncate text if it's too long for the cell
  const maxChars = fontSize > 0 ? Math.floor(cellWidth / (fontSize * 0.6)) : 0;
  const displayName = name && name.length > maxChars ? name.substring(0, maxChars - 3) + '...' : name;

  // Check if this cell has navigation configured
  const cellNav = getNavigationForElement?.(undefined, name);
  const hasCellNavigation = isNavigable && cellNav;

  return (
    <g>
      <rect
        x={cellX}
        y={cellY}
        width={cellWidth}
        height={cellHeight}
        style={{
          fill: cellDepth < 2 ? colors[index % colors.length] : colors[(index + 3) % colors.length],
          stroke: "#fff",
          strokeWidth: 2,
          strokeOpacity: 1,
          cursor: hasCellNavigation ? 'pointer' : 'default',
        }}
        onClick={(e) => {
          e.stopPropagation();
          onCellClick?.({ name, [valueKey]: value, index, depth: cellDepth });
        }}
      />
      {/* Add a clipPath to prevent text overflow */}
      <defs>
        <clipPath id={`clip-${cellX}-${cellY}`}>
          <rect x={cellX} y={cellY} width={cellWidth} height={cellHeight} />
        </clipPath>
      </defs>
      <g clipPath={`url(#clip-${cellX}-${cellY})`}>
        {fontSize > 0 && displayName && (
          <text
            x={cellX + cellWidth / 2}
            y={showValue ? cellY + cellHeight / 2 - 6 : cellY + cellHeight / 2}
            textAnchor="middle"
            dominantBaseline="middle"
            fill="#fff"
            fontSize={fontSize}
            fontWeight="bold"
            style={{ pointerEvents: 'none' }}
          >
            {displayName}
          </text>
        )}
        {showValue && value != null && (
          <text
            x={cellX + cellWidth / 2}
            y={cellY + cellHeight / 2 + fontSize - 2}
            textAnchor="middle"
            dominantBaseline="middle"
            fill="#fff"
            fontSize={fontSize - 2}
            opacity={0.9}
            style={{ pointerEvents: 'none' }}
          >
            {typeof value === 'number' ? value.toLocaleString() : String(value)}
          </text>
        )}
      </g>
    </g>
  );
};

export const TreemapChart: React.FC<TreemapChartProps> = ({
  component,
  isPreview = false,
}) => {
  const { properties = {}, style = {}, size, responsivePosition } = component;
  const containerRef = useRef<HTMLDivElement>(null);
  const breakpoint = useResponsiveViewport();

  // Track palette changes via CSS variable mutations
  const [paletteChangeCounter, setPaletteChangeCounter] = useState(0);

  console.log('🗺️ TreemapChart - Component Props:', {
    componentId: component.id,
    componentName: component.name,
    size: size,
    style: style,
    breakpoint: breakpoint,
    isPreview: isPreview
  });

  // Merge breakpoint-specific properties with base properties
  const breakpointProps = responsivePosition?.breakpoints?.[breakpoint]?.properties || {};
  const mergedProperties = { ...properties, ...breakpointProps };

  console.log('🗺️ TreemapChart - Merged Properties:', {
    baseProperties: properties,
    breakpointProps: breakpointProps,
    mergedProperties: mergedProperties,
    minHeight: mergedProperties.minHeight,
    height: mergedProperties.height
  });

  // Get dataSource configuration
  const dataSource = mergedProperties.dataSource as ChartDataSource | undefined;
  const templateId = mergedProperties.queryTemplateId as string | undefined;
  const hasQueryTemplate = isQueryTemplateDataSource(dataSource);

  // Execute Query Template if configured
  const {
    data: queryTemplateData,
    isLoading: qtLoading,
    error: qtError,
    refetch: qtRefetch,
    lastFetched,
  } = useQueryTemplateData({
    dataSource,
    componentId: component.id,
    enabled: hasQueryTemplate || !!templateId,
    templateId,
    initialData: mergedProperties.data as Record<string, unknown>[] | undefined,
  });

  // Loading/error state from query template
  const loading = qtLoading;
  const error = qtError;

  // Data priority: Query template > Static data > Default data
  const chartData = queryTemplateData
    || (mergedProperties.data && Array.isArray(mergedProperties.data) ? mergedProperties.data : defaultData);

  // Dynamic key extraction for TreemapChart — validate against actual data, auto-detect if needed
  const queryNameKey = hasQueryTemplate && dataSource?.queryDefinition ? getXAxisKeyFromQuery(dataSource.queryDefinition) : null;
  const autoNameKey = chartData.length > 0
    ? Object.keys(chartData[0]).find(k => typeof chartData[0][k] === 'string')
    : null;
  // Validate explicit nameKey against actual data — fall through if it doesn't match
  const explicitNameKey = (mergedProperties.nameKey as string) || (mergedProperties.xAxisKey as string) || null;
  const validatedExplicitNameKey = explicitNameKey && chartData.length > 0 && explicitNameKey in chartData[0]
    ? explicitNameKey : null;
  const nameKey = validatedExplicitNameKey
    || (queryNameKey && chartData.length > 0 && queryNameKey in chartData[0] ? queryNameKey : null)
    || autoNameKey
    || queryNameKey
    || "name";

  const queryValueKeys = hasQueryTemplate && dataSource?.queryDefinition ? getLineKeysFromQuery(dataSource.queryDefinition) : null;
  const validatedTreeValue = queryValueKeys?.[0] && chartData.length > 0 && queryValueKeys[0] in chartData[0]
    ? queryValueKeys[0] : null;
  const autoTreeValue = chartData.length > 0
    ? Object.keys(chartData[0]).find(key => key !== nameKey && typeof chartData[0][key] === 'number')
    : null;
  // Validate explicit valueKey against actual data — fall through if it doesn't match
  const propertyLineKeys = Array.isArray(mergedProperties.lineKeys)
    ? mergedProperties.lineKeys
    : undefined;
  const explicitValueKey = (mergedProperties.valueKey as string) || (propertyLineKeys?.[0] as string | undefined) || null;
  const validatedExplicitValueKey = explicitValueKey && chartData.length > 0 && explicitValueKey in chartData[0]
    ? explicitValueKey : null;
  const valueKey = validatedExplicitValueKey
    || validatedTreeValue
    || autoTreeValue
    || queryValueKeys?.[0]
    || "size";

  // Get size configuration from component.size (no ResizeObserver needed)
  const sizeConfig = getChartSizeConfig(
    toNumericDimension(size?.width),
    toNumericDimension(size?.height)
  );

  console.log('🗺️ TreemapChart - Size Config:', sizeConfig);

  // Get responsive configuration
  const title = mergedProperties.title === null || mergedProperties.title === undefined || mergedProperties.title === ""
    ? undefined
    : (mergedProperties.title as string);
  const description = mergedProperties.description as string;

  const responsiveConfig = getChartResponsiveConfig(
    sizeConfig,
    !!title,
    !!description,
    undefined,
    false, // Treemap doesn't typically show legend
    false  // Treemap doesn't use grid
  );

  // Resolve CSS variables in colors to actual color values for Recharts
  const [resolvedColors, setResolvedColors] = useState<string[]>(() => {
    const colors = (mergedProperties.colors as string[]) || defaultColors;
    return colors;
  });

  // Watch for CSS variable changes on ancestor with data-palette-scope
  useLayoutEffect(() => {
    const findPaletteScope = (element: HTMLElement | null): HTMLElement | null => {
      while (element) {
        if (element.dataset.paletteScope === 'true') return element;
        element = element.parentElement;
      }
      return null;
    };

    const paletteScope = findPaletteScope(containerRef.current);
    if (!paletteScope) return;

    const observer = new MutationObserver(() => {
      setPaletteChangeCounter(c => c + 1);
    });

    observer.observe(paletteScope, {
      attributes: true,
      attributeFilter: ['style']
    });

    return () => observer.disconnect();
  }, []);

  // Resolve colors after DOM is mounted to ensure palette scope CSS variables are accessible
  // Re-resolve when palette changes to update CSS variable values
  useLayoutEffect(() => {
    const colors = (mergedProperties.colors as string[]) || defaultColors;
    const resolved = resolveColorArray(colors, containerRef.current);
    setResolvedColors(resolved);
  }, [mergedProperties.colors, paletteChangeCounter]);

  const chartConfig: ChartConfig = {
    title,
    data: chartData,
    xAxisKey: nameKey,
    yAxisKey: valueKey,
    showTooltip: mergedProperties.showTooltip !== false,
    colors: resolvedColors,
    responsive: mergedProperties.responsive !== false,
    animation: mergedProperties.animation !== false,
  };

  // Chart navigation hooks for click-through navigation
  const interaction = useComponentInteraction(component);
  const { handleChartClick, isNavigable, getNavigationForElement } = useChartNavigation(component, interaction);

  // Calculate minimum height for the chart area
  const dynamicMinimum = sizeConfig.isVerySmall ? 40 : sizeConfig.isSmall ? 70 : 90;

  console.log('🗺️ TreemapChart - Dynamic Minimum:', {
    dynamicMinimum,
    isVerySmall: sizeConfig.isVerySmall,
    isSmall: sizeConfig.isSmall,
    showTitle: responsiveConfig.showTitle
  });

  // Container style - use responsive height from style or component.size
  // Don't override with 100% - use the clamp() values from the template
  const heightValue = (typeof style?.height === 'string' || typeof style?.height === 'number')
    ? style.height
    : (typeof size?.height === 'string' || typeof size?.height === 'number')
    ? size.height
    : '100%';

  console.log('🗺️ TreemapChart - Height Value Selection:', {
    componentId: component.id,
    styleHeight: style?.height,
    sizeHeight: size?.height,
    selectedHeightValue: heightValue
  });

  // Strip width-related properties from component.style - sizing is handled by ComponentWrapper/FlexContainer
  const { width: _w, minWidth: _mw, maxWidth: _xw, ...visualStyle } = (style || {}) as React.CSSProperties;
  const containerStyle: React.CSSProperties = {
    position: 'relative',
    width: '100%',
    height: heightValue,
    minHeight: (typeof mergedProperties.minHeight === 'string' || typeof mergedProperties.minHeight === 'number')
      ? mergedProperties.minHeight
      : `${dynamicMinimum + (responsiveConfig.showTitle ? 40 : 0)}px`,
    display: 'flex',
    flexDirection: 'column',
    ...visualStyle,  // Apply other style properties after base styles (without width-related)
  };

  console.log('🗺️ TreemapChart - Container Style:', {
    containerStyle,
    styleFromComponent: style,
    calculatedMinHeight: containerStyle.minHeight
  });

  console.log('🗺️ TreemapChart - Responsive Config:', responsiveConfig);

  return (
    <div className="w-full h-full" style={containerStyle} ref={containerRef}>
      <NavigationIndicator isNavigable={isNavigable && !isPreview} />
      {responsiveConfig.showTitle && (
        <div className={`text-center ${responsiveConfig.titleMargin} relative`}>
          <h3 className={responsiveConfig.titleClass}>{chartConfig.title}</h3>
          {responsiveConfig.showDescription && (
            <p className="text-xs text-muted-foreground">
              {description}
            </p>
          )}
          {/* Refresh button for query template data sources */}
          {hasQueryTemplate && !isPreview && (
            <button
              onClick={() => qtRefetch()}
              className="absolute right-2 top-0 p-1 rounded hover:bg-muted/50 transition-colors"
              title={lastFetched ? `Last updated: ${lastFetched.toLocaleTimeString()}` : 'Refresh data'}
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 text-muted-foreground ${loading ? 'animate-spin' : ''}`} />
            </button>
          )}
        </div>
      )}

      <div style={{ position: 'relative', width: '100%', flex: 1, minHeight: 0 }}>
        {loading ? (
          <ChartLoadingSkeleton variant="treemap" />
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-full space-y-2">
            <AlertCircle className="w-8 h-8 text-red-500" />
            <p className="text-sm text-red-600">{error}</p>
          </div>
        ) : chartData && chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <Treemap
              data={chartData}
              dataKey={valueKey}
              nameKey={nameKey}
              aspectRatio={4 / 3}
              stroke="#fff"
              fill="#8884d8"
              content={
                <CustomizedContent
                  colors={chartConfig.colors || defaultColors}
                  valueKey={valueKey}
                  onCellClick={(cellProps: TreemapCellPayload) => {
                    handleChartClick(cellProps, cellProps.name, cellProps.index);
                  }}
                  isNavigable={isNavigable}
                  getNavigationForElement={getNavigationForElement}
                />
              }
              animationDuration={chartConfig.animation ? 300 : 0}
            >
              {chartConfig.showTooltip && (
                <Tooltip
                  contentStyle={{
                    backgroundColor: "hsl(var(--popover))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: "6px",
                  }}
                />
              )}
            </Treemap>
          </ResponsiveContainer>
        ) : (
          <div className="flex items-center justify-center h-full">
            <p className="text-muted-foreground">No chart data available</p>
          </div>
        )}
      </div>
    </div>
  );
};
